import { ICianMFUIElement } from '@cian/mf-registry/browser';
import { IMicrofrontendManifest } from '@cian/mf-registry/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApplicationContext } from 'shared/containers/ApplicationContext';
import { registerNewbuildingBrokerModalsMicroFrontendManifest } from 'shared/services/registerNewbuildingBrokerModalsMicroFrontendManifest';
import {
  INewbuildingBrokerModalsMicroFrontendAPI,
  IOpenDIYModalParams,
} from 'shared/types/microfrontends/newbuildingBrokerModals';

import { TOpenMicrofrontResultHandler } from '../types';

export function useMicrofrontAPI(resultCallback: TOpenMicrofrontResultHandler) {
  const { httpApi, logger, microfrontendsRegistry } = useApplicationContext();

  const microfrontRef = useRef<ICianMFUIElement>(null);
  const [microfrontManifest, setMicrofrontManifest] = useState<IMicrofrontendManifest>();

  const getLocalAPI = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (): INewbuildingBrokerModalsMicroFrontendAPI => (microfrontRef.current?.instance?.customElement as any)?.api,
    [microfrontRef],
  );

  const paramsRef = useRef<IOpenDIYModalParams | undefined>(undefined);
  const firstAPICallRef = useRef(true);

  useEffect(() => {
    // Логика выполняется один раз при первом вызове локального API после установки манифеста в стейт.
    if (microfrontManifest && firstAPICallRef.current) {
      firstAPICallRef.current = false;

      // Хак, чтобы подождать рендера `<Microfrontend />` и инициализации рефа для локального API.
      setTimeout(() => {
        const api = getLocalAPI();

        if (api) {
          api['/v1/open-self-request-modal/'](paramsRef.current as IOpenDIYModalParams);
          resultCallback({ isError: false });
        } else {
          resultCallback({ isError: true });
        }
      }, 100);
    }
  }, [resultCallback, getLocalAPI, logger, microfrontManifest]);

  const registerAndSetManifest = useCallback(
    async (params: IOpenDIYModalParams) => {
      const result = await registerNewbuildingBrokerModalsMicroFrontendManifest({
        httpApi,
        logger,
        microfrontendsRegistry,
      });

      if (result) {
        // Сохраняем в реф, чтобы вызвать с этими параметрами после установки в стейт и инициализации рефа.
        paramsRef.current = params;

        setMicrofrontManifest(result);
      } else {
        resultCallback({ isError: true });
      }
    },
    [httpApi, logger, microfrontendsRegistry, resultCallback],
  );

  const openMicrofront = useCallback(
    async (params: IOpenDIYModalParams): Promise<void> => {
      if (microfrontManifest) {
        await getLocalAPI()['/v1/open-self-request-modal/'](params);
        resultCallback({ isError: false });
      } else {
        await registerAndSetManifest(params);
      }
    },
    [getLocalAPI, registerAndSetManifest, resultCallback, microfrontManifest],
  );

  return {
    microfrontManifest,
    microfrontRef,
    openMicrofront,
  };
}
